/**
 * The XP/level hero — big level badge + a progress bar toward the next
 * level + total XP. XP uses the brand accent (a different axis from the
 * skill-band colors, which measure *how good* you are, not *how much
 * you've put in*).
 */

import { ICON_STROKE, Zap } from "@/shared/components/icons";
import type { Gamification } from "@/features/gamification/lib/gamification";

export function XpMeterHero({ data }: { data: Gamification }) {
  const pct =
    data.levelSpan > 0
      ? Math.min(100, Math.round((data.intoLevel / data.levelSpan) * 100))
      : 0;
  const toNext = Math.max(0, data.levelSpan - data.intoLevel);

  return (
    <section className="rounded-2xl border border-border bg-surface p-6 space-y-5">
      <div className="flex items-center gap-4">
        <div className="relative w-16 h-16 shrink-0 rounded-2xl bg-accent/15 border border-accent/30 flex flex-col items-center justify-center">
          <Zap size={18} strokeWidth={ICON_STROKE} className="text-accent" />
          <span className="text-lg font-bold text-accent leading-none tabular-nums">
            {data.level}
          </span>
        </div>
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wider text-text-muted">
            Player level
          </div>
          <div className="text-2xl font-bold text-text tracking-tight">
            Level {data.level}
          </div>
          <div className="text-sm text-text-muted tabular-nums">
            {data.totalXp.toLocaleString()} XP total
          </div>
        </div>
      </div>

      <div className="space-y-1.5">
        <div className="h-2.5 rounded-full bg-bg/60 overflow-hidden">
          <div
            className="h-full rounded-full bg-accent transition-[width] duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="flex justify-between text-[11px] text-text-muted tabular-nums">
          <span>Level {data.level}</span>
          <span>
            {toNext.toLocaleString()} XP to level {data.level + 1}
          </span>
        </div>
      </div>
    </section>
  );
}
